import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import ContentCard from '../../../../../components/layout/ContentCard/ContentCard';
import Table from '../../../../../components/data/Table/Table';
import SearchFilter from '../../../../../components/data/SearchFilter';
import Pagination from '../../../../../components/data/Pagination/Pagination';
import Badge from '../../../../../components/common/Badge/Badge';
import Button from '../../../../../components/common/Button/Button';
import { translate } from '../../../../../utils/Common';
import { usePlatforms } from '../../../../../hooks/usePlatforms';
import * as FaIcons from 'react-icons/fa';
import styles from './CompaniesTab.module.css';

const EMPTY_FILTERS = {};
const EMPTY_FILTER_DEFS = [];

const CompaniesTab = ({ showError, userIdGL, onCountChange, onLoadingChange, canSearch = true }) => {
  const history = useHistory();
  const { getAllAssignments } = usePlatforms();
  const [assignments, setAssignments] = useState([]);
  // Starts true: between the first render and the effect firing, a false
  // flag with an empty list paints the "nothing here" state for a frame,
  // which reads as the screen finding nothing and then changing its mind.
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (!userIdGL) return;

    const loadAssignments = async () => {
      if (onLoadingChange) {
        onLoadingChange(true);
      }
      setLoading(true);

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const liveController = new AbortController();
      abortControllerRef.current = liveController;

      try {
        const result = await getAllAssignments(userIdGL, abortControllerRef.current.signal);
        const list = Array.isArray(result) ? result : [];
        setAssignments(list);
        if (onCountChange) {
          onCountChange(list.length);
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error loading assignments:', err);
          if (showError) {
            showError(err.message || translate('dashboard.clients.detail.companies.errors.loadError'));
          }
          setAssignments([]);
          if (onCountChange) {
            onCountChange(0);
          }
        }
      } finally {
        // Only the request that was not superseded may clear the flag: an
        // aborted one used to switch it off for its replacement, blanking the
        // view until the new data arrived.
        if (!liveController.signal.aborted) setLoading(false);
        if (onLoadingChange) {
          onLoadingChange(false);
        }
      }
    };

    loadAssignments();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [userIdGL, getAllAssignments, showError]);

  // Filtrar assignments basado en búsqueda y filtros
  const filteredAssignments = useMemo(() => {
    let result = [...assignments];

    // Aplicar búsqueda general
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(assignment => {
        const code = (assignment.companyCode || '').toString().toLowerCase();
        const name = (assignment.companyName || '').toString().toLowerCase();
        
        return code.includes(searchLower) || name.includes(searchLower);
      });
    }

    return result;
  }, [assignments, searchTerm]);

  // Paginación sobre resultados filtrados
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedAssignments = filteredAssignments.slice(startIndex, endIndex);
  const filteredTotalPages = Math.ceil(filteredAssignments.length / itemsPerPage);

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  const handleRowClick = (row) => {
    const companyCode = row?.companyCode;
    if (companyCode) {
      history.push(`/dashboard/companies/${companyCode}`);
    }
  };

  const columns = [
    {
      key: 'companyCode',
      label: translate('dashboard.clients.detail.companies.columns.code'),
      sortable: true,
      width: '200px',
      render: (row) => row?.companyCode || '-'
    },
    {
      key: 'companyName',
      label: translate('dashboard.clients.detail.companies.columns.name'),
      sortable: true,
      width: '240px',
      render: (row) => row?.companyName || '-'
    },
    {
      key: 'actions',
      label: '',
      width: '140px',
      sortable: false,
      render: (row) => {
        const companyCode = row?.companyCode;
        return (
          <Button
            variant="ghostOutline"
            size="sm"
            leftIcon={<FaIcons.FaExternalLinkAlt size={12} />}
            onClick={(e) => {
              e.stopPropagation();
              if (companyCode) {
                history.push(`/dashboard/companies/${companyCode}`);
              }
            }}
          >
            {translate('global.view') || 'View'}
          </Button>
        );
      }
    }
  ];

  return (
    <div>
      <div className={styles.filtersContainer}>
        <div className={styles.searchFilterWrapper}>
          {canSearch && (
            <SearchFilter
              placeholder={translate('dashboard.clients.detail.companies.filters.searchPlaceholder')}
              onSearch={handleSearch}
              filters={EMPTY_FILTER_DEFS}
              activeFilters={EMPTY_FILTERS}
            />
          )}
        </div>
      </div>

      <Table
        columns={columns}
        data={paginatedAssignments}
        loading={loading}
        emptyMessage={translate('dashboard.clients.detail.companies.empty')}
        hoverable
        onRowClick={handleRowClick}
      />

      {!loading && filteredAssignments.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={filteredTotalPages}
          totalItems={filteredAssignments.length}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      )}
    </div>
  );
};

export default CompaniesTab;

